import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import DictationInput from '../DictationInput';
import { toast } from 'sonner';
import { Mic, Sparkles, Send, Loader2 } from 'lucide-react';

export default function MockInterviewTab({ api }) {
  const [interviewState, setInterviewState] = useState({ step: 'start', question: '', response: '', feedback: '', followUp: '', finalFeedback: '', loading: false });

  const startInterview = async () => {
    setInterviewState(s => ({...s, loading: true}));
    try {
      const r = await api.post('/ai/mock-interview', { round: 1 });
      setInterviewState({ step: 'q1', question: r.data.question, response: '', feedback: '', followUp: '', finalFeedback: '', loading: false });
    } catch {
      toast.error('Failed to start');
      setInterviewState(s => ({...s, loading: false}));
    }
  };

  const submitRound1 = async () => {
    setInterviewState(s => ({...s, loading: true}));
    try {
      const r = await api.post('/ai/mock-interview', { question: interviewState.question, response: interviewState.response, round: 1 });
      setInterviewState(s => ({...s, step: 'q2', feedback: r.data.feedback, followUp: r.data.follow_up_question, response: '', loading: false}));
    } catch {
      toast.error('Failed');
      setInterviewState(s => ({...s, loading: false}));
    }
  };

  const submitRound2 = async () => {
    setInterviewState(s => ({...s, loading: true}));
    try {
      const r = await api.post('/ai/mock-interview', { question: interviewState.followUp, response: interviewState.response, round: 2 });
      setInterviewState(s => ({...s, step: 'done', finalFeedback: r.data.final_feedback, loading: false}));
    } catch {
      toast.error('Failed');
      setInterviewState(s => ({...s, loading: false}));
    }
  };

  const reset = () => setInterviewState({ step: 'start', question: '', response: '', feedback: '', followUp: '', finalFeedback: '', loading: false });

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-base font-semibold flex items-center gap-2"><Mic className="w-4 h-4 text-violet-600" />AI Mock Interview</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Practice answering interview questions. AI will give you supportive feedback.</p>
          {interviewState.step === 'start' && (
            <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2" data-testid="start-mock-interview" disabled={interviewState.loading} onClick={startInterview}>
              {interviewState.loading ? <><Loader2 size={14} className="animate-spin" /> Starting...</> : <><Sparkles size={14} /> Start Mock Interview</>}
            </Button>
          )}
          {interviewState.step === 'q1' && (
            <div className="space-y-3" data-testid="mock-q1">
              <div className="bg-violet-50 rounded-xl p-4 border border-violet-200"><p className="text-sm font-medium text-violet-900">{interviewState.question}</p></div>
              <DictationInput value={interviewState.response} onChange={(cleaned) => setInterviewState(s => ({...s, response: cleaned}))} rows={3} placeholder="Your answer..." data-testid="mock-q1-response" />
              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2" disabled={!interviewState.response || interviewState.loading} data-testid="submit-mock-r1" onClick={submitRound1}>
                {interviewState.loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit Answer
              </Button>
            </div>
          )}
          {interviewState.step === 'q2' && (
            <div className="space-y-3" data-testid="mock-q2">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200"><p className="text-sm text-emerald-800">{interviewState.feedback}</p></div>
              <div className="bg-violet-50 rounded-xl p-4 border border-violet-200"><p className="text-sm font-medium text-violet-900">{interviewState.followUp}</p></div>
              <DictationInput value={interviewState.response} onChange={(cleaned) => setInterviewState(s => ({...s, response: cleaned}))} rows={3} placeholder="Your improved answer..." data-testid="mock-q2-response" />
              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2" disabled={!interviewState.response || interviewState.loading} data-testid="submit-mock-r2" onClick={submitRound2}>
                {interviewState.loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit Final Answer
              </Button>
            </div>
          )}
          {interviewState.step === 'done' && (
            <div className="space-y-3" data-testid="mock-done">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200"><p className="text-sm text-emerald-800 whitespace-pre-line">{interviewState.finalFeedback}</p></div>
              <Button variant="outline" className="rounded-full w-full" onClick={reset}>Practice Again</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
